<template>
  <TjingSidebar
    :show="show"
    @close="closeSidebar()"
    :preventClickOutside="true"
    :width="{
      mobile: 'calc(100% - 35px)',
      tablet: '400px',
      laptop: '500px',
      desktop: '500px',
    }"
  >
    <template v-slot:header>
      <div class="sidebar-header">
        <p class="sidebar-title">
          <b>Custom points system</b>
        </p>
      </div></template
    >
    <template v-slot:body>
      <div class="sidebar-body" v-if="loaded">
        <p class="sidebar-body-title">
          <b v-if="pointsId">Edit custom points system</b
          ><b v-else>
            <template v-if="overrideMode">
              Create division-level points systems
            </template>
            <template v-else> Create custom points system </template>
          </b>
        </p>
        <p class="sidebar-body-subtext">
          <template v-if="pointsId">Adjust this points system.</template>
          <template v-else>Define points for each position.</template>
        </p>
        <div class="points-wrapper">
          <p class="points-title">Pos</p>
          <p class="points-points">Points</p>

          <div class="position" v-for="position in positions">
            <p>
              <b>{{ position.place }}</b>
            </p>
            <TjingInput
              v-model="position.points"
              type="number"
              :bold="true"
              :center="true"
              :showError="position.points < 0"
            ></TjingInput>
            <span
              v-if="position.place === positions.length && positions.length > 1"
              @click="removePosition()"
              ><Remove
            /></span>
          </div>
        </div>
        <div class="add-position" @click="addPosition()">+</div>
        <div class="subsequent-points">
          <p><b>Subsequent positions</b></p>
          <TjingInput
            v-model="defaultPoint"
            type="number"
            :bold="true"
            :showError="defaultPoint < 0 || defaultPoint > 999"
          />
          <p>
            Set default points for all subsequent positions not specified above.
          </p>
        </div>
        <div class="tied-points">
          <p><b>Tied points</b></p>
          <p class="subtext">
            Determine how points are shared when positions are tied.
          </p>
          <TjingRadioButtons
            v-model="ties"
            :options="[
              {
                title: 'By highest points',
                value: 'HIGHEST',
              },
              {
                title: 'By lowest points',
                value: 'LOWEST',
              },
              {
                title: 'By average points',
                value: 'AVERAGE',
              },
            ]"
          />
        </div>
        <div class="info-block" v-if="!overrideMode && !pointsId">
          <p>
            Once this base system is created, you can add division-level points
            systems if needed.
          </p>
        </div>
        <div class="divisions-container" v-if="overrideMode">
          <p><b>Divisions</b></p>
          <p class="subtext">Select which divisions these points apply to.</p>
          <div class="divisions">
            <div
              class="division"
              :class="{
                disabled: division.disabled,
                selected: divisions.includes(division.id),
              }"
              v-for="division in availableDivisions"
              @click="toggleDivision(division.id, division.disabled)"
            >
              <Checkmark v-if="divisions.includes(division.id)" color="white" />
              <p>
                <b>{{ division.type }}</b>
              </p>
            </div>
          </div>
        </div>
        <div
          class="delete"
          :class="{ disabled: disableDelete }"
          v-if="pointsId"
          @click="!disableDelete && deleteSystem(pointsId)"
        >
          <p>Delete custom system</p>
        </div>
      </div>
    </template>
    <template v-slot:footer>
      <div class="sidebar-footer">
        <TjingButton title="Cancel" @click="$emit('cancel')" />
        <TjingButton
          title="Save"
          variant="solid"
          @click="save()"
          :disabled="!isSaveable"
        />
      </div>
    </template>
  </TjingSidebar>
</template>

<script>
import Remove from '@/assets/icons/Remove'
import TjingSidebar from '@/pages/TjingSidebar'
import TjingButton from '@/components/UIElements/TjingButton.vue'
import TjingInput from '@/components/UIElements/TjingInput.vue'
import TjingRadioButtons from '@/components/UIElements/TjingRadioButtons'
import Checkmark from '@/assets/icons/Checkmark'
export default {
  name: 'CustomPointsSidebar',
  props: {
    show: {
      type: Boolean,
      default: false,
      required: true,
    },
    pointsId: {
      type: String,
    },
    tourId: {
      type: String,
      required: true,
    },
    eventId: {
      type: String,
    },
    overrideMode: {
      type: Boolean,
      default: false,
    },
    availableDivisions: {
      type: Array,
      required: true,
    },
    disableDelete: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TjingSidebar,
    Remove,
    TjingButton,
    TjingInput,
    TjingRadioButtons,
    Checkmark,
  },
  data() {
    return {
      loaded: false,
      currentPoints: null,
      defaultPoint: null,
      ties: 'HIGHEST',
      positions: [],
      divisions: [],
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(val) {
        if (val) {
          this.positions = []
          this.ties = 'HIGHEST'
          this.defaultPoint = null
          this.divisions = []
          this.loadData(this.pointsId)
        }
      },
    },
    pointsId: {
      handler(newVal, oldVal) {
        // Reset when pointsId changes (especially when it becomes null for new system)
        if (this.show && newVal !== oldVal) {
          this.positions = []
          this.ties = 'HIGHEST'
          this.defaultPoint = null
          this.divisions = []
          this.loadData(newVal)
        }
      },
    },
  },
  computed: {
    isSaveable() {
      let hasErrors = false
      this.positions.forEach((p) => {
        if (!p.points) {
          p.showError = true
          hasErrors = true
        } else if (p.points < 0 || p.points > 1000) {
          p.showError = true
          hasErrors = true
        } else {
          p.showError = false
        }
      })
      if (!this.defaultPoint && this.defaultPoint !== 0) {
        hasErrors = true
      } else if (this.defaultPoint < 0 || this.defaultPoint > 1000) {
        hasErrors = true
      }

      if (this.overrideMode) {
        if (this.divisions.length === 0) {
          hasErrors = true
        }
      }

      if (hasErrors) {
        return false
      }

      return true
    },
  },
  methods: {
    toggleDivision(divisionId, disabled) {
      if (disabled) {
        return // Don't allow toggling disabled divisions
      }
      if (this.divisions.includes(divisionId)) {
        this.divisions = this.divisions.filter((id) => id !== divisionId)
      } else {
        this.divisions.push(divisionId)
      }
    },
    addPosition() {
      this.positions.push({ place: this.positions.length + 1, points: null })
    },
    removePosition() {
      this.positions.pop()
    },
    async save() {
      if (this.pointsId) {
        this.updateSystem(this.pointsId)
      } else {
        this.createSystem(this.tourId, this.eventId)
      }
    },
    async createSystem(tourId, eventId) {
      const pointsMap = this.positions.map((p) => {
        return { position: p.place, points: p.points }
      })
      try {
        const system = await this.$axios({
          headers: {
            Authorization: this.$store.state.user.sessionInfo.token,
          },
          data: {
            query: `
            mutation CreateCustomLeaderboardSystem($tourId: ID!, $input: CreateEventPointsMapInput!){
              CreateCustomLeaderboardSystem(tourId: $tourId, input: $input){
                id
              }
            }
            `,
            variables: {
              tourId: tourId,
              input: {
                tiePoint: this.ties,
                positions: pointsMap,
                defaultPoint: this.defaultPoint,
                eventId,
                divisionIds: this.overrideMode ? this.divisions : undefined,
              },
            },
          },
        })

        if (system.data.errors) {
          throw system.data.errors[0]
        }

        if (!eventId) {
          await this.updateTourSettings(tourId)
        }

        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Custom points system created',
          type: 'success',
        })
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: err.message || 'Error',
          type: 'error',
        })
      } finally {
        this.$emit('refresh')
      }
    },
    async updateSystem(pointsId) {
      const pointsMap = this.positions.map((p) => {
        return { position: p.place, points: p.points }
      })
      try {
        const system = await this.$axios({
          headers: {
            Authorization: this.$store.state.user.sessionInfo.token,
          },
          data: {
            query: `
            mutation UpdateCustomLeaderboardSystem($systemId: ID!, $input: UpdateEventPointsMapInput!){
              UpdateCustomLeaderboardSystem(systemId: $systemId, input: $input){
                id
              }
            }
            `,
            variables: {
              systemId: pointsId,
              input: {
                tiePoint: this.ties,
                positions: pointsMap,
                defaultPoint: this.defaultPoint,
                divisionIds: this.overrideMode ? this.divisions : undefined,
              },
            },
          },
        })

        if (system.data.errors) {
          throw system.data.errors[0]
        }

        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Updated custom points system',
          type: 'success',
        })
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: err.message || 'Error',
          type: 'error',
        })
      } finally {
        this.$emit('refresh')
      }
    },
    async deleteSystem(pointsId) {
      try {
        const system = await this.$axios({
          headers: {
            Authorization: this.$store.state.user.sessionInfo.token,
          },
          data: {
            query: `
            mutation{
              DeleteLeaderboardPointsMap(systemId: "${pointsId}")
            }
            `,
          },
        })

        if (system.data.errors) {
          throw system.data.errors[0]
        }

        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Point system removed',
          type: 'success',
        })
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: err.message || 'Error',
          type: 'error',
        })
      } finally {
        this.$emit('refresh')
      }
    },
    async loadData(pointsId) {
      if (!pointsId) {
        this.positions.push({ place: 1, points: null })
        this.loaded = true
        return
      }

      const tour = await this.$axios({
        headers: { Authorization: this.$store.state.user.sessionInfo.token },
        data: {
          query: `
            {
              customPointSystem(systemId:"${pointsId}"){
                id
                tourId
                eventId
                positions{
                  place
                  points
                }
                defaultPoint
                tiePoints
                createdAt
                updatedAt
                divisions{
                  id
                  type
                  name
                }
              }
            }
          `,
        },
      })
      this.currentPoints = tour.data.data.customPointSystem
      this.ties = this.currentPoints.tiePoints
      this.defaultPoint = this.currentPoints.defaultPoint
      this.positions = this.currentPoints.positions
      // Initialize divisions array with divisions assigned to this system
      if (
        this.currentPoints.divisions &&
        this.currentPoints.divisions.length > 0
      ) {
        this.divisions = this.currentPoints.divisions.map((d) => d.id)
      } else {
        this.divisions = []
      }
      this.loaded = true
    },
    async updateTourSettings(tourId) {
      try {
        const updatedTour = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
            mutation UpdateTour($UpdateTour: UpdateTourInput!){
              UpdateTour(tourId:"${this.$router.currentRoute.params.tourId}", input:$UpdateTour){
                id
              }
            }
          `,
            variables: {
              UpdateTour: {
                scoringType: 'CUSTOM_POINTS',
              },
            },
          },
        })
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Error',
          type: 'error',
        })
      }
    },
    closeSidebar() {
      this.$emit('close')
    },
  },
  mounted() {},
  beforeCreate() {},
  destroyed() {},
}
</script>

<style lang="scss" scoped>
@import '@/styles/Typography';
@import '@/styles/Sidebars';
@import '@/styles/manager-settings';

.info-block {
  margin-top: 24px;
  background: $mist;
  font-size: 1rem;
  line-height: 1.5rem;
  border-radius: 6px;
  padding: 16px;
}

.divisions-container {
  .divisions {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 1.5rem;
  }
}

.division {
  padding: 7px 12px;
  border-radius: 16px;
  border: 1px solid $midnight;
  background-color: $mist;
  cursor: pointer;
  transition: all ease 0.3s;
  gap: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  &:hover {
    background-color: $fog;
  }
  &.disabled {
    pointer-events: none;
    opacity: 0.2;
  }
  &.selected {
    background: $midnight;
    b {
      color: white;
    }
  }

  svg {
    height: 12px;
    width: auto;
  }
}

.default-radio-buttons {
  margin-top: 1.5rem;
}
.tied-points,
.divisions-container {
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid $sleet;

  .subtext {
    margin-top: 0.625rem;
  }

  p {
    font-size: 1rem;
    color: $strom;

    b {
      color: $midnight;
    }
  }
}
.subsequent-points {
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid $sleet;
  display: grid;
  grid-template-columns: 3fr 1fr;
  gap: 0 48px;

  p {
    font-size: 1rem;
    color: $strom;

    b {
      color: $midnight;
    }

    &:last-child {
      margin-top: 0.625rem;
    }
  }
  .tjing-input {
    grid-column: 2;
    grid-row: 1/3;
    height: 48px;
  }
}

.add-position {
  user-select: none;
  width: 48px;
  height: 48px;
  margin-top: 12px;
  background-color: $midnight;
  cursor: pointer;
  transition: all ease 0.3s;
  border-radius: 6px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;

  &:hover {
    background-color: black;
  }
}
.points-wrapper {
  margin-top: 32px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.points-title {
  width: 48px;
  text-align: center;
  color: $strom;
  font-size: 0.875rem;
}
.points-points {
  width: 128px;
  text-align: center;
  color: $strom;
  font-size: 0.875rem;
}

.position {
  width: 100%;
  display: flex;
  gap: 8px;
  align-items: center;

  p {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: $mist;
    border-radius: 6px;
  }
  span {
    cursor: pointer;
    margin-left: 6px;
    svg {
      width: 20px;
      height: 20px;
    }
  }
  .tjing-input {
    width: 128px;
  }
}

.sidebar-body-title {
  font-size: 1.5rem;
}
.sidebar-body-subtext {
  font-size: 1rem;
  color: $strom;
  margin-top: 0.625rem;
}

.sidebar-footer {
  flex-wrap: wrap;

  .tjing-button {
    width: auto;
  }
}
.delete {
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid $sleet;
  text-align: center;
  p {
    display: inline-block;
    cursor: pointer;
    color: $dusk;
  }
  &.disabled {
    pointer-events: none;
    opacity: 0.5;
    p {
      cursor: not-allowed;
      color: $blizzard;
    }
  }
}

@media (min-width: 480px) and (orientation: landscape) {
}

@media (min-width: 544px) {
}

@media (min-width: 768px) {
}

@media (min-width: 1200px) {
}
</style>
